import { useState, useEffect, useMemo } from 'react';
import ArtZipButton from './ArtZipButton';

const EXPECTED_CODES_PER_ORDER = 25;
const COLLAPSED_ROWS = 8;

/**
 * Searchable list of every MSE batch with CSV + art zip downloads.
 * Fetches its own data (the batch query aggregates ~300k codes and takes a
 * couple of seconds) so the dashboard's initial render isn't blocked.
 */
export default function BatchExports({ refreshKey }) {
  const [batches, setBatches] = useState(null);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    Promise.all([
      fetch('/api/greenlight/batches').then((r) => r.json()),
      fetch('/api/greenlight/history').then((r) => r.json()),
    ])
      .then(([batchData, historyData]) => {
        if (cancelled) return;
        if (batchData && batchData.error) {
          setError(batchData.error);
          setBatches([]);
          return;
        }
        setBatches(Array.isArray(batchData) ? batchData : []);
        setHistory(Array.isArray(historyData) ? historyData : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message);
          setBatches([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const logByLabel = useMemo(() => {
    const map = {};
    for (const h of history) map[h.batch_label] = h;
    return map;
  }, [history]);

  const filtered = useMemo(() => {
    if (!batches) return [];
    const q = search.trim().toLowerCase();
    if (!q) return batches;
    return batches.filter((b) => b.label.toLowerCase().includes(q));
  }, [batches, search]);

  const searching = search.trim().length > 0;
  const visible = showAll || searching ? filtered : filtered.slice(0, COLLAPSED_ROWS);
  const hiddenCount = filtered.length - visible.length;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mt-6">
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900">Batch Exports</h2>
            {batches && (
              <span className="text-xs text-gray-400">
                {searching ? `${filtered.length} of ${batches.length}` : `${batches.length} MSE batches`}
              </span>
            )}
          </div>
          <div className="relative w-64">
            <svg
              className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search batches..."
              className="w-full pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="px-5 py-4 text-sm text-red-700 bg-red-50 border-b border-red-100">{error}</div>
      )}

      {!batches && !error && (
        <div className="px-5 py-8 flex items-center justify-center gap-2.5 text-sm text-gray-400">
          <div className="w-4 h-4 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
          Loading batches...
        </div>
      )}

      {batches && filtered.length === 0 && (
        <div className="px-5 py-8 text-center text-sm text-gray-400">
          {searching ? `No batches matching "${search}"` : 'No MSE batches found'}
        </div>
      )}

      {batches && filtered.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Batch</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Greenlight</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Orders</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">QR Codes</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Applied Here</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Downloads</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((b, idx) => {
                  // The 25-codes-per-order rule only applies to MSE full kits;
                  // MSP store orders are free-form and log-only batches have no count
                  const expected = b.orderCount * EXPECTED_CODES_PER_ORDER;
                  const checkable = b.source === 'MSE' && b.codeCount != null;
                  const complete = !checkable || b.codeCount === expected;
                  const log = logByLabel[b.label];
                  return (
                    <tr
                      key={b.label}
                      className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} border-b border-gray-100/70`}
                    >
                      <td className="px-5 py-3 font-mono text-xs font-medium text-gray-900">{b.label}</td>
                      <td className="px-5 py-3 text-gray-500 text-xs">
                        {b.greenlightDate
                          ? new Date(b.greenlightDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              timeZone: 'UTC',
                            })
                          : '—'}
                      </td>
                      <td className="px-5 py-3 text-gray-600 tabular-nums">{b.orderCount}</td>
                      <td className="px-5 py-3">
                        <span className={`tabular-nums ${complete ? 'text-gray-600' : 'text-amber-700 font-medium'}`}>
                          {b.codeCount ?? '—'}
                        </span>
                        {!complete && (
                          <span
                            className="ml-1.5 text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded"
                            title={`Expected ${expected} (${b.orderCount} orders x ${EXPECTED_CODES_PER_ORDER})`}
                          >
                            {b.codeCount > expected ? '+' : ''}
                            {b.codeCount - expected}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs">
                        {log ? (
                          <span className="text-emerald-600 font-medium">
                            {new Date(log.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            {log.skipped_count > 0 && (
                              <span className="ml-1.5 text-amber-600" title={log.skipped_dealer_ids || ''}>
                                ({log.skipped_count} skipped)
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {/^BB.*_cards(_\d+)?$/.test(b.label) ? (
                          <span className="text-xs text-gray-400" title="Backbook cards are pre-produced — nothing to print. The stands batch file references them.">
                            pre-produced
                          </span>
                        ) : (
                        <div className="flex items-center gap-3">
                          <a
                            href={`/api/greenlight/export/${encodeURIComponent(b.label)}`}
                            download
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                            CSV
                          </a>
                          <ArtZipButton
                            label={b.label}
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                            </svg>
                            Art ZIP
                          </ArtZipButton>
                        </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!searching && (hiddenCount > 0 || showAll) && (
            <div className="px-5 py-3 border-t border-gray-100">
              <button
                onClick={() => setShowAll(!showAll)}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
              >
                {showAll ? 'Show recent only' : `Show all ${filtered.length} batches`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
